// src/pages/admin/news/NewsDetail.js
import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import AdminLayout from "../../../layouts/admin/AdminLayout";
import api from "../../../api/axios";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

export default function AdminNewsDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { t } = useTranslation();

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // 편집용 상태
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [infoMsg, setInfoMsg] = useState("");

  const fmtDate = (d) => {
    if (!d) return "";
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return d;
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const dd = String(dt.getDate()).padStart(2, "0");
    return `${yyyy}.${mm}.${dd}`;
  };

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      // 🔹 상세 조회 (백엔드: GET /api/news/detail/{id})
      const res = await api.get(`/api/news/detail/${id}`);
      const resultCode = res.data?.resultCode;

      if (resultCode !== "200" && resultCode !== 200) {
        setError(
          res.data?.resultMessage || "뉴스&이벤트 상세를 불러오지 못했습니다."
        );
        setItem(null);
        return;
      }

      const data = res.data?.data || null;
      setItem(data);
      // 편집용 초기값 세팅
      setTitle(data?.title || "");
      setContent(data?.content || "");
    } catch (e) {
      console.error(e);
      setError(
        e.response?.data?.resultMessage ||
          "뉴스&이벤트 상세를 불러오는 중 오류가 발생했습니다."
      );
      setItem(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const renderContent = () => {
    if (!item?.content) return null;
    const safeHtml = DOMPurify.sanitize(item.content);
    return (
      <div
        className="prose max-w-none text-sm leading-relaxed text-slate-800 prose-a:text-blue-600 prose-a:underline prose-img:rounded-xl prose-img:shadow-sm md:text-base"
        dangerouslySetInnerHTML={{ __html: safeHtml }}
      />
    );
  };

  // 🔹 수정 모드 진입
  const onEdit = () => {
    if (!item) return;
    setTitle(item.title || "");
    setContent(item.content || "");
    setInfoMsg("");
    setError("");
    setEditing(true);
  };

  // 🔹 수정 취소
  const onCancelEdit = () => {
    if (item) {
      setTitle(item.title || "");
      setContent(item.content || "");
    }
    setEditing(false);
    setInfoMsg("");
  };

  // 🔹 수정 저장
  const onSave = async () => {
    setError("");
    setInfoMsg("");

    if (!title.trim()) {
      setError("제목을 입력해 주세요.");
      return;
    }

    const plain = content.replace(/<[^>]+>/g, "").trim();
    if (!plain) {
      setError("내용을 입력해 주세요.");
      return;
    }

    setSaving(true);
    try {
      const sanitizedContent = DOMPurify.sanitize(content);

      // ⚠️ 백엔드: PUT /api/news/{id} 기준
      const payload = {
        title: title.trim(),
        content: sanitizedContent,
      };

      const res = await api.put(`/api/news/${id}`, payload, {
        headers: { "Content-Type": "application/json" },
      });

      const resultCode = res.data?.resultCode;
      if (resultCode === "200" || resultCode === 200) {
        setInfoMsg("뉴스&이벤트이 수정되었습니다.");
        setEditing(false);
        // 최신 내용 다시 반영
        const updated = res.data?.data;
        if (updated) {
          setItem(updated);
          setTitle(updated.title || "");
          setContent(updated.content || "");
        } else {
          // 응답에 data가 없으면 재조회
          load();
        }
      } else {
        setError(
          res.data?.resultMessage ||
            "뉴스&이벤트 수정에 실패했습니다. 다시 시도해 주세요."
        );
      }
    } catch (e) {
      console.error(e);
      setError(
        e.response?.data?.resultMessage ||
          "뉴스&이벤트 수정 중 오류가 발생했습니다."
      );
    } finally {
      setSaving(false);
    }
  };

  // 🔹 삭제
  const onDelete = async () => {
    if (!window.confirm("정말 이 뉴스&이벤트을 삭제하시겠습니까?")) return;

    setDeleting(true);
    setError("");
    setInfoMsg("");
    try {
      // ⚠️ 백엔드: DELETE /api/news/{id} 기준
      const res = await api.delete(`/api/news/${id}`);
      const resultCode = res.data?.resultCode;
      if (resultCode === "200" || resultCode === 200) {
        alert("뉴스&이벤트이 삭제되었습니다.");
        nav("/admin/news/list");
      } else {
        setError(
          res.data?.resultMessage ||
            "뉴스&이벤트 삭제에 실패했습니다. 다시 시도해 주세요."
        );
      }
    } catch (e) {
      console.error(e);
      setError(
        e.response?.data?.resultMessage ||
          "뉴스&이벤트 삭제 중 오류가 발생했습니다."
      );
    } finally {
      setDeleting(false);
    }
  };

  // Quill 옵션 (작성 화면과 동일하게)
  const quillModules = {
    toolbar: [
      [{ header: "1" }, { header: "2" }, { font: [] }],
      [{ size: [] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link", "image"],
      ["clean"],
    ],
  };

  const quillFormats = [
    "header",
    "font",
    "size",
    "bold",
    "italic",
    "underline",
    "strike",
    "color",
    "background",
    "list",
    "bullet",
    "link",
    "image",
  ];

  return (
    <AdminLayout>
      {/* 헤더 영역 */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-blue-900 md:text-3xl">
            {t("adminNews.detailTitle", "뉴스&이벤트 상세")}
          </h1>
          <p className="mt-1 text-xs text-slate-500">ID: {id}</p>
        </div>

        <div className="flex gap-2">
          {!editing && (
            <>
              <button
                type="button"
                onClick={onEdit}
                className="rounded-xl border bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                수정
              </button>
              <button
                type="button"
                onClick={onDelete}
                disabled={deleting}
                className="rounded-xl border border-rose-300 bg-rose-50 px-4 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-100 disabled:opacity-60"
              >
                {deleting ? "삭제 중..." : "삭제"}
              </button>
            </>
          )}

          {editing && (
            <>
              <button
                type="button"
                onClick={onCancelEdit}
                className="rounded-xl border bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                취소
              </button>
              <button
                type="button"
                onClick={onSave}
                disabled={saving}
                className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {saving ? "저장 중..." : "저장"}
              </button>
            </>
          )}

          <button
            type="button"
            onClick={() => nav("/admin/news/list")}
            className="rounded-xl border bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            {t("adminNews.toList", "목록으로")}
          </button>
        </div>
      </div>

      {/* 본문 카드 */}
      <div className="rounded-2xl border bg-white p-6 shadow-sm md:p-8">
        {/* 상태 */}
        {loading && (
          <div className="py-10 text-center text-slate-500">
            {t("news.loading", "뉴스&이벤트을 불러오는 중입니다...")}
          </div>
        )}

        {!loading && error && (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {error}
          </div>
        )}

        {infoMsg && !loading && (
          <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">
            {infoMsg}
          </div>
        )}

        {!loading && !error && !item && (
          <div className="py-10 text-center text-slate-500">
            {t("news.notFound", "해당 뉴스&이벤트을 찾을 수 없습니다.")}
          </div>
        )}

        {!loading && !error && item && (
          <>
            {/* ====== 읽기 모드 ====== */}
            {!editing && (
              <>
                {/* 제목 + 메타 */}
                <header className="border-b pb-4 md:pb-5">
                  <div className="mb-1 text-xs text-slate-500">
                    작성일 : {fmtDate(item.createdAt)}
                    {item.updatedAt && (
                      <>
                        {"  ·  "}수정일 : {fmtDate(item.updatedAt)}
                      </>
                    )}
                  </div>
                  <h2 className="text-xl font-extrabold tracking-tight text-slate-900 md:text-2xl">
                    {item.title}
                  </h2>
                </header>

                {/* 내용 */}
                <section className="mt-6">{renderContent()}</section>

                {/* 프론트 페이지에서 보기 링크 */}
                <div className="mt-8 flex justify-between border-t pt-4 text-xs text-slate-500">
                  <div>ID: {item.id}</div>
                  <Link
                    to={`/news/${item.id}`}
                    rel="noreferrer"
                    target="_blank"
                    className="inline-flex items-center rounded-lg border bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                  >
                    사용자 화면에서 보기
                  </Link>
                </div>
              </>
            )}

            {/* ====== 편집 모드 ====== */}
            {editing && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  onSave();
                }}
                className="space-y-4"
              >
                {/* 메타 정보 */}
                <div className="text-xs text-slate-500">
                  작성일 : {fmtDate(item.createdAt)}
                  {item.updatedAt && (
                    <>
                      {"  ·  "}수정일 : {fmtDate(item.updatedAt)}
                    </>
                  )}
                </div>

                {/* 제목 */}
                <div className="grid gap-1 text-sm">
                  <span className="font-medium text-slate-800">
                    제목<span className="text-rose-600"> *</span>
                  </span>
                  <input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="제목을 입력해 주세요."
                  />
                </div>

                {/* 내용 (Quill) */}
                <div className="grid gap-1 text-sm">
                  <span className="font-medium text-slate-800">
                    내용<span className="text-rose-600"> *</span>
                  </span>
                  <div className="rounded-xl border">
                    <ReactQuill
                      theme="snow"
                      value={content}
                      onChange={setContent}
                      modules={quillModules}
                      formats={quillFormats}
                      placeholder="뉴스&이벤트 내용을 입력해 주세요."
                    />
                  </div>
                  <span className="text-xs text-slate-500">
                    간단한 서식, 링크 등을 사용할 수 있습니다.
                  </span>
                </div>
              </form>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}
